import React, { useEffect, useState } from "react";
import moment from "moment";
import { useParams } from "react-router-dom";
import { faChampagneGlasses, faFileContract } from "@fortawesome/free-solid-svg-icons";
import {
    Content,
    Card,
    CardBody,
    Row,
    Col,
    ContentLoader,
    Button,
    LoadingSpinner,
    Alert,
    SmallValueBox,
    CardHeader,
    Table,
} from "@webtypen/react-components";
import { Page } from "../../components/Page";
import { ApiHelper, ConfirmHelper } from "../../helpers";
import { CommentsBox } from "../../components/CommentsBox";

export const EmployeesShiftsDetails = () => {
    const params = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [isStarting, setIsStarting] = useState(false);
    const [data, setData] = useState(null);

    const startShift = async () => {
        if (data.begin_actual) {
            return;
        }

        const status = await ConfirmHelper.confirm("Soll die Schicht wirklich gestartet werden?");
        if (!status) {
            return null;
        }

        setIsStarting(true);
        ApiHelper.request("/api/employees-shifts/start", {
            _id: params.id,
        })
            .then((response) => {
                if (response.status !== "success") {
                    ConfirmHelper.alert(
                        response.status === "error" && response.message && response.message.trim() !== ""
                            ? response.message
                            : "Es ist ein unerwarteter Fehler aufgetreten ..."
                    );
                }

                load(() => {
                    setIsStarting(false);
                });
            })
            .catch((e) => {
                console.error(e);
                ConfirmHelper.alert(
                    e && e.data && e.data.status === "error" && e.data.message && e.data.message.trim() !== ""
                        ? e.data.message
                        : "Es ist ein unerwarteter Fehler aufgetreten ..."
                );
                load(() => {
                    setIsStarting(false);
                });
            });
    };

    const endShift = async () => {
        if (!data.begin_actual || data.end_actual) {
            return;
        }

        const status = await ConfirmHelper.confirm("Soll die Schicht wirklich beendet werden?");
        if (!status) {
            return null;
        }

        setIsStarting(true);
        ApiHelper.request("/api/employees-shifts/end", {
            _id: params.id,
        })
            .then((response) => {
                if (response.status !== "success") {
                    ConfirmHelper.alert(
                        response.status === "error" && response.message && response.message.trim() !== ""
                            ? response.message
                            : "Es ist ein unerwarteter Fehler aufgetreten ..."
                    );
                }

                load(() => {
                    setIsStarting(false);
                });
            })
            .catch((e) => {
                console.error(e);
                ConfirmHelper.alert(
                    e && e.data && e.data.status === "error" && e.data.message && e.data.message.trim() !== ""
                        ? e.data.message
                        : "Es ist ein unerwarteter Fehler aufgetreten ..."
                );
                load(() => {
                    setIsStarting(false);
                });
            });
    };

    const load = (callback) => {
        setIsLoading(true);
        ApiHelper.request("/api/employees-shifts/details", {
            _id: params.id,
        })
            .then((response) => {
                setData(response.status === "success" && response.data && response.data._id ? response.data : null);
                setIsLoading(false);

                if (callback) {
                    callback();
                }
            })
            .catch((e) => {
                console.error(e);
                setData(null);
                setIsLoading(false);

                if (callback) {
                    callback();
                }
            });
    };

    useEffect(() => {
        load();
    }, [params.id]);

    if (isLoading || !data) {
        return (
            <Page icon={faChampagneGlasses} title="Deine Jobs / Schichten" back="/employees-shifts">
                <Content>
                    {isLoading ? <ContentLoader /> : <Alert danger>Fehler beim Laden des Datensatzes ...</Alert>}
                </Content>
            </Page>
        );
    }

    const today = moment().format("YYYY-MM-DD");
    const now = moment().format("HH:mm");
    return (
        <Page
            icon={faFileContract}
            title={
                moment(data.date).format("DD.MM.YYYY") +
                (data.begin_planned && data.begin_planned >= moment().format("HH:mm")
                    ? ": " + data.begin_planned + (data.end_planned ? " - " + data.end_planned : "")
                    : "")
            }
            back="/employees-shifts"
        >
            <Content>
                {data.date === today && !data.begin_actual && now >= data.begin_planned ? (
                    <Button type="button" className="mb-3" disabled={isStarting} block onClick={startShift}>
                        {isStarting ? <LoadingSpinner /> : "Schicht starten"}
                    </Button>
                ) : null}

                {data.date && data.begin_actual && !data.end_actual ? (
                    <Button type="button" className="mb-3" disabled={isStarting} block onClick={endShift}>
                        {isStarting ? <LoadingSpinner /> : "Schicht beenden"}
                    </Button>
                ) : null}

                <Row>
                    <Col width={3}>
                        <Card className="mb-3">
                            <CardBody>
                                <SmallValueBox label="Kunde" value={data.customer_object.name} />
                            </CardBody>
                        </Card>
                    </Col>
                    <Col width={3}>
                        <Card className="mb-3">
                            <CardBody>
                                <SmallValueBox
                                    label="Rolle / Typ"
                                    value={data.type_object && data.type_object.name ? data.type_object.name : "-"}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                    <Col width={3} className="mb-3">
                        <Card>
                            <CardBody>
                                <SmallValueBox
                                    label="Geplante Arbeitszeit"
                                    value={
                                        data.begin_planned
                                            ? data.begin_planned + (data.end_planned ? " bis " + data.end_planned : "")
                                            : null
                                    }
                                />
                            </CardBody>
                        </Card>
                    </Col>
                    <Col width={3}>
                        <Card className="mb-3">
                            <CardBody>
                                <SmallValueBox
                                    label="Tatsächliche Arbeitszeit"
                                    value={
                                        data.begin_actual
                                            ? data.begin_actual + (data.end_actual ? " bis " + data.end_actual : "")
                                            : null
                                    }
                                />
                            </CardBody>
                        </Card>
                    </Col>
                    <Col width={3}>
                        <Card className="mb-3">
                            <CardBody>
                                <SmallValueBox
                                    label="Adresse"
                                    value={
                                        (data.contract_object && data.contract_object.address_street) ||
                                        (data.contract_object && data.contract_object.address_zip) ||
                                        (data.contract_object && data.contract_object.address_city) ? (
                                            <div>
                                                <div>{data.contract_object.address_street}</div>
                                                <div>
                                                    {data.contract_object.address_zip}{" "}
                                                    {data.contract_object.address_city}
                                                </div>
                                            </div>
                                        ) : (
                                            "-"
                                        )
                                    }
                                />
                            </CardBody>
                        </Card>
                    </Col>

                    {data.contract_object &&
                    data.contract_object.note_internal &&
                    data.contract_object.note_internal.trim() !== "" ? (
                        <Col width={12}>
                            <Card>
                                <CardBody style={{ whiteSpace: "pre" }}>{data.contract_object.note_internal}</CardBody>
                            </Card>
                        </Col>
                    ) : null}
                    <Col width={12}></Col>

                    <Col width={6} className="pt-3">
                        <CommentsBox title="Kommentare" channel={"contracts_" + data.contract_object._id} />
                    </Col>
                    <Col width={6} className="pt-3">
                        <Card>
                            <CardHeader>Mitarbeiter</CardHeader>
                            <Table>
                                <tbody>
                                    {data.employees ? (
                                        data.employees.map((employee, i) => (
                                            <tr key={i}>
                                                <td>{employee.name}</td>
                                                <td style={{ width: 100, opacity: 0.7 }}>
                                                    {employee.type ? employee.type : "-"}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td>Es wurden noch keine Mitarbeiter eingeplant ...</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card>
                    </Col>
                </Row>
            </Content>
        </Page>
    );
};
